import { useState } from 'react';
import { Button, FormField, Input } from '@playerbase/ui';
import {
  PLAYTEST_EVENT_BUTTON_LABEL_MAX,
  PLAYTEST_EVENT_HEADLINE_MAX,
  PLAYTEST_EVENT_LABEL_MAX,
  PLAYTEST_EVENT_NAME_MAX,
  PLAYTEST_EVENT_NAME_PATTERN,
} from '@playerbase/types';

import {
  type BuilderEvent,
  type UsePlaytestBuilderState,
} from '../../../../games/use-playtest-builder-state.js';
import { FieldsCard } from '../../../feedback-settings/builder/fields-card.js';
import { eventFieldsAdapter } from './event-fields-adapter.js';

interface EventRowProps {
  event: BuilderEvent;
  builder: UsePlaytestBuilderState;
}

const FIELD_LABEL_CLASS =
  'font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted';

// Per-event card. Wraps the form-builder's FieldsCard for the modal
// questions and surfaces the event-level inputs the tester actually sees
// (headline + DM button label) plus the SDK-facing name.
export function EventRow({ event, builder }: EventRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const summary =
    event.label !== '' ? event.label : event.name !== '' ? event.name : 'New event';
  const adapter = eventFieldsAdapter(event, builder);
  // Live identifier-shape validation. The save RPC re-validates; this is
  // just so the studio sees the problem at the input rather than after
  // pressing Save. Empty is treated as "not entered yet", not invalid.
  const nameShapeError =
    event.name !== '' && !PLAYTEST_EVENT_NAME_PATTERN.test(event.name)
      ? 'Use letters, digits and underscores only. Must start with a letter or underscore.'
      : null;

  return (
    <li className="rounded-md border border-border bg-surface-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <div className="flex min-w-0 flex-col">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Event
          </span>
          <span className="truncate text-sm font-semibold text-text">{summary}</span>
        </div>
        <span className="flex items-center gap-2 text-xs text-text-muted">
          <span>
            {event.questions.length} question{event.questions.length === 1 ? '' : 's'}
          </span>
          <span aria-hidden>{expanded ? '▾' : '▸'}</span>
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-border px-4 py-4">
          <div className="flex flex-col gap-5">
            <section className="grid gap-3 md:grid-cols-2">
              <FormField>
                <FormField.Label className={FIELD_LABEL_CLASS} data-required>
                  Event label
                </FormField.Label>
                <FormField.Control>
                  <Input
                    value={event.label}
                    onChange={(e) =>
                      builder.updateEvent(event.clientId, { label: e.target.value })
                    }
                    maxLength={PLAYTEST_EVENT_LABEL_MAX}
                    placeholder="Tutorial complete"
                  />
                </FormField.Control>
                <FormField.HelpText>
                  Human-readable name for your team. We&rsquo;ll auto-generate
                  the SDK identifier from it.
                </FormField.HelpText>
              </FormField>
              <FormField>
                <FormField.Label className={FIELD_LABEL_CLASS} data-required>
                  SDK event name
                </FormField.Label>
                <FormField.Control>
                  <Input
                    value={event.name}
                    onChange={(e) =>
                      builder.updateEvent(event.clientId, { name: e.target.value })
                    }
                    maxLength={PLAYTEST_EVENT_NAME_MAX}
                    placeholder="tutorialComplete"
                    aria-invalid={nameShapeError !== null}
                  />
                </FormField.Control>
                <FormField.HelpText>
                  {nameShapeError !== null ? (
                    <span className="text-danger-text">{nameShapeError}</span>
                  ) : (
                    <>
                      Auto-filled from the label; edit if you want a different
                      identifier in your code.
                    </>
                  )}
                </FormField.HelpText>
              </FormField>
            </section>

            <section className="flex flex-col gap-3 rounded-md border border-border bg-surface px-4 py-3">
              <header className="flex flex-col gap-0.5">
                <h4 className="text-sm font-semibold text-text">Tester DM</h4>
                <p className="text-xs text-text-muted">
                  What the bot sends when this event fires. Tapping the button
                  opens a modal with the questions below.
                </p>
              </header>
              <FormField>
                <FormField.Label className={FIELD_LABEL_CLASS} data-required>
                  Headline
                </FormField.Label>
                <FormField.Control>
                  <Input
                    value={event.headline}
                    onChange={(e) =>
                      builder.updateEvent(event.clientId, { headline: e.target.value })
                    }
                    maxLength={PLAYTEST_EVENT_HEADLINE_MAX}
                    placeholder="How was the tutorial?"
                  />
                </FormField.Control>
                <FormField.HelpText>
                  Shown in the DM bubble and as the modal title. Up to{' '}
                  {PLAYTEST_EVENT_HEADLINE_MAX} characters (Discord modal title cap).
                </FormField.HelpText>
              </FormField>
              <FormField>
                <FormField.Label className={FIELD_LABEL_CLASS}>
                  Button label
                </FormField.Label>
                <FormField.Control>
                  <Input
                    value={event.buttonLabel}
                    onChange={(e) =>
                      builder.updateEvent(event.clientId, { buttonLabel: e.target.value })
                    }
                    maxLength={PLAYTEST_EVENT_BUTTON_LABEL_MAX}
                    placeholder="Share feedback"
                  />
                </FormField.Control>
              </FormField>
            </section>

            <FieldsCard builder={adapter} />

            <FormField>
              <FormField.Label className={FIELD_LABEL_CLASS}>Notes</FormField.Label>
              <FormField.Control>
                <Input
                  value={event.description ?? ''}
                  onChange={(e) =>
                    builder.updateEvent(event.clientId, {
                      description: e.target.value === '' ? null : e.target.value,
                    })
                  }
                  placeholder="Optional context for your team."
                />
              </FormField.Control>
            </FormField>

            <div className="flex justify-end">
              <Button
                variant="ghost"
                type="button"
                onClick={() => builder.removeEvent(event.clientId)}
              >
                Remove event
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
}
